/**
 * Risk Assessment Module
 *
 * Provides comprehensive maritime risk management capabilities
 * with Lite (workboat) and Comprehensive (merchant vessel) tiers.
 *
 * @module risk-assessment
 */

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * Feature flags for tier detection and feature gating
 * @type {Object}
 */
export { FeatureFlags } from './config/featureFlags';

/**
 * Risk assessment configuration (risk levels, likelihood/severity scales, etc.)
 * @type {Object}
 */
export {
  RISK_LEVELS,
  LIKELIHOOD_SCALE,
  SEVERITY_SCALE,
  CONTROL_HIERARCHY,
  ALARP_ZONES,
  REVIEW_FREQUENCIES,
  STATUS_OPTIONS,
  ASSESSMENT_TYPES,
  MODULE_SETTINGS,
  getRiskLevel,
  getAlarpZone,
  calculateRiskScore,
  getRiskMatrixColor,
  validateRiskAssessment
} from './config/moduleConfig';

// ============================================================================
// MODELS
// ============================================================================

/**
 * RiskAssessment data model
 * @class
 */
export { RiskAssessment } from './models/RiskAssessment';

/**
 * Hazard data model
 * @class
 */
export { Hazard } from './models/Hazard';

// ============================================================================
// SERVICES
// ============================================================================

// NOTE: riskAssessmentService removed - storage logic is now in useRiskAssessments hook
// following the SeaReady useStorageState pattern for vessel-scoped localStorage
export { riskCalculationService } from './services/riskCalculationService';

// ============================================================================
// HOOKS
// ============================================================================

export { useRiskAssessments } from './hooks/useRiskAssessments';
export { useFeatureFlags } from './hooks/useFeatureFlags';

// ============================================================================
// COMPONENTS
// ============================================================================

export { RiskAssessmentModule } from './components/RiskAssessmentModule';
export { RiskAssessmentForm } from './components/RiskAssessmentForm';
export { RiskAssessmentSidebar } from './components/RiskAssessmentSidebar';
export { RiskAssessmentWizard } from './components/RiskAssessmentWizard'; // Legacy modal version

// ============================================================================
// DATA
// ============================================================================

export { workboatTasks, getTasksByCategory, getTaskById } from './data/workboatTasks';

// ============================================================================
// UTILITIES
// ============================================================================

export { riskMatrixCalculator } from './utils/riskMatrixCalculator';
export { alarpLogic } from './utils/alarpLogic';

// ============================================================================
// MODULE METADATA
// ============================================================================

/**
 * Module metadata
 */
export const MODULE_INFO = {
  name: 'risk-assessment',
  version: '1.0.0',
  tier: 'dual', // Supports both Lite and Comprehensive
  deployment: 'dual', // Integrated + Standalone capable
  status: 'development',
  compliance: ['WBC3', 'ISM', 'SOLAS']
};
